import React, {useEffect, useMemo, useState} from 'react'
import {Welcome} from './Welcome'
import {toAbsoluteUrl} from '../../../../_gori/helpers'
import OnboardService from '../core/_requests'
import useCancelToken from '../../../../_gori/hooks/UseCancelToken'
import {useAuth} from '../../auth'
import {PROFILE_FIELD, STEPS} from '../core/_const'
import {Onboarding, Pricing, SelectedPlan} from '../../onboard'
import axios from 'axios'
import {useFormik} from 'formik'
import UserService from '../../users/core/_requests'
import {ROUTES} from '../../../../_gori/constants'
import * as Yup from 'yup'
import {forEach, isEmpty} from 'lodash'
import useYupValidate from '../../../../_gori/hooks/UseYupValidate'
import {useNavigate} from 'react-router-dom'
import {ValidationErrorModal} from '../../../../_gori/partials/widgets'

const OnboardPage: React.FC = () => {
  const {newCancelToken, isCancel} = useCancelToken()
  const {currentUser} = useAuth()
  const [activeStep, setActiveStep] = useState<number>(STEPS.welcome)
  const [onboard, setOnboard] = useState<any>([])
  const [prepareData, setPrepareData] = useState<any>({})
  const SIGNATURE_ACCESS_KEY = process.env.REACT_APP_SIGNATURE_ACCESS_KEY

  const [currentTier, setCurrentTier] = useState<string>('free')
  const [saveAndExit, setSaveAndExit] = useState<boolean>(false)
  const {stringYup, regexYup} = useYupValidate()
  const {setCurrentUser} = useAuth()
  const navigate = useNavigate()
  const [validationErrors, setValidationErrors] = useState<any>()
  const [disable, setDisable] = useState<boolean>(false)

  useEffect(() => {
    const fetchData = async () => {
      try {
        axios.defaults.headers.common[`${SIGNATURE_ACCESS_KEY}`] =
          currentUser?.current_account?.encrypt_key
        const data = await OnboardService.get({cancelToken: newCancelToken()})
        setOnboard(data)

        // Initialize currentTier from onboard data if available
        if (data?.plan_tier) {
          setCurrentTier(data.plan_tier)
        }
      } catch (error) {
        if (isCancel(error)) return
        console.error('Error fetching data:', error)
      }
    }

    fetchData()

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentUser?.current_account?.encrypt_key])

  const initialValues = useMemo(() => {
    let init: any = {}
    forEach(PROFILE_FIELD.SALES, (field: any) => {
      if (!isEmpty(onboard)) {
        init[field.name] = onboard?.profile?.[`${field.name}`]
      }
    })

    return {
      ...init,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onboard])

  const validationSchema = useMemo(() => {
    return Yup.object().shape({
      sales_rep: regexYup.inputText('SALE_REP', false, 50),
      promo_code: stringYup(50, 'PROMO_CODE', false),
    })
  }, [regexYup, stringYup])

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: async () => {
      setDisable(true)
      await handleSubmit()
    },
  })

  const handleSubmit = async () => {
    let payload = {
      step: 'pricing',
      data: {
        ...prepareData,
        ...formik.values,
        plan_tier: currentTier,
      },
      save_and_exit: saveAndExit,
    }

    try {
      const config = {cancelToken: newCancelToken()}
      await OnboardService.store(payload, config)

      if (saveAndExit) {
        navigate(ROUTES.AUTH.LOGOUT)
      } else {
        setCurrentUser(await UserService.getUser(config))
        navigate(ROUTES.ORDERS.BASE)
      }
      setDisable(false)
    } catch (error: any) {
      setDisable(false)
      if (isCancel(error)) return
      setValidationErrors(error?.response)
    }
  }

  const sectionComponent = useMemo(() => {
    switch (activeStep) {
      case STEPS.welcome:
        return <Welcome setActiveStep={setActiveStep} />
      case STEPS.single_step:
        return (
          <Onboarding
            setActiveStep={setActiveStep}
            onboard={onboard}
            prepareData={prepareData}
            setPrepareData={setPrepareData}
            currentTier={currentTier}
            originalFormik={formik}
            disable={disable}
            setSaveAndExit={setSaveAndExit}
            saveAndExit={saveAndExit}
          />
        )
      case STEPS.pricing:
        return (
          <Pricing
            setActiveStep={setActiveStep}
            formik={formik}
            currentTier={currentTier}
            setCurrentTier={setCurrentTier}
            setSaveAndExit={setSaveAndExit}
          />
        )
      case STEPS.selected_plan:
        return (
          <SelectedPlan
            setActiveStep={setActiveStep}
            formik={formik}
            setSaveAndExit={setSaveAndExit}
          />
        )
      default:
        return <Welcome setActiveStep={setActiveStep} />
    }
  }, [activeStep, onboard, prepareData, currentTier, formik, disable, saveAndExit])

  return (
    <div id='onboard' className='onboard'>
      <div className='onboard-logo'>
        <img
          alt='Logo'
          src={toAbsoluteUrl('/media/logos/big-shipbae-logo.png')}
          className='h-45px'
        />
      </div>

      {validationErrors && (
        <ValidationErrorModal
          handleClose={() => {
            setValidationErrors(undefined)
          }}
          response={validationErrors}
        />
      )}
      <div className='container'>{sectionComponent}</div>
    </div>
  )
}

export {OnboardPage}
